import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Utensils,
  CalendarDays,
  MapPin,
  Clock,
  ArrowRight,
  Power,
  Lock,
  BarChart3,
  QrCode,
  Sparkles,
  Crown,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, hint, testid, accent = false }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Icon size={16} className={accent ? "text-[#E8650A]" : ""} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function PlanBadge({ tier }) {
  const isPro = (tier || "basic").toLowerCase() === "pro";
  if (isPro) {
    return (
      <span
        data-testid="plan-badge-pro"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#E8650A] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider shadow-sm"
      >
        <Crown className="h-3.5 w-3.5" /> Pro Plan
      </span>
    );
  }
  return (
    <span
      data-testid="plan-badge-basic"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#E8650A]/30 bg-[#FDECE2] text-[#E8650A] px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
    >
      <CheckCircle2 className="h-3.5 w-3.5" /> Basic Plan
    </span>
  );
}

const PRO_FEATURES = [
  {
    key: "analytics",
    title: "Advanced Analytics",
    description: "Track foot traffic, peak hours, and revenue trends across locations.",
    icon: BarChart3,
  },
  {
    key: "qr-code",
    title: "Custom QR Code",
    description: "Branded QR codes for your truck — direct customers to your live menu.",
    icon: QrCode,
  },
  {
    key: "featured-map",
    title: "Featured on Map",
    description: "Stand out with priority placement and a featured pin on the public map.",
    icon: Sparkles,
  },
];

function ProFeaturesGrid({ tier }) {
  const isPro = (tier || "basic").toLowerCase() === "pro";
  return (
    <section className="space-y-3" data-testid="pro-features-section">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Pro features</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isPro ? "All Pro features unlocked." : "Upgrade to unlock advanced tools that grow your truck."}
          </p>
        </div>
        {!isPro && (
          <Button
            className="ff-btn-primary h-9"
            data-testid="upgrade-pro-cta"
            onClick={() => toast.info("Pro plan upgrade is coming soon — we'll email you when it's live.")}
          >
            <Crown className="h-4 w-4 mr-1.5" /> Upgrade to Pro
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PRO_FEATURES.map(({ key, title, description, icon: Icon }) => (
          <div
            key={key}
            data-testid={`pro-feature-${key}${isPro ? "-unlocked" : "-locked"}`}
            className={`relative rounded-xl border p-5 transition ${
              isPro
                ? "bg-white border-slate-200 hover:border-[#E8650A]/40"
                : "bg-slate-50 border-slate-200"
            }`}
          >
            <div className="flex items-center gap-2 text-slate-700">
              <div className={`h-9 w-9 rounded-lg grid place-items-center ${isPro ? "bg-[#FDECE2] text-[#E8650A]" : "bg-white border border-slate-200 text-slate-400"}`}>
                <Icon size={18} />
              </div>
              <div className="font-semibold text-sm">{title}</div>
              {!isPro && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <Lock className="h-3 w-3" /> Pro
                </span>
              )}
            </div>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{description}</p>
            {!isPro && (
              <div className="mt-4 text-xs font-semibold text-[#E8650A]">Available on Pro</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { vendor, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/today");
      setData(data);
    } catch (err) {
      console.error("[Dashboard] load failed:", err?.message || err);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGoOffline = async () => {
    setBusy(true);
    try {
      await api.post("/vendors/me/go-offline");
      toast.success("You are now offline");
      await refresh();
      await load();
    } catch (err) {
      console.error("[Dashboard] go-offline failed:", err?.message || err);
      toast.error("Failed to go offline");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-slate-500 text-sm">Loading dashboard…</div>;
  }

  const isLive = !!data?.is_live;
  const todayHours = data?.today_hours;
  const hoursLabel = !todayHours
    ? "Not set"
    : todayHours.is_closed
    ? "Closed today"
    : `${todayHours.open_time || "—"} → ${todayHours.close_time || "—"}`;
  const currentLocName = data?.current_location?.name || data?.current_location?.address || "—";

  return (
    <div className="space-y-8" data-testid="vendor-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hey, {vendor?.business_name || "Vendor"}</h1>
          <p className="text-slate-500 mt-1 text-sm">{"Here's what's happening today."}</p>
        </div>
        <PlanBadge tier={vendor?.tier} />
      </div>

      {/* Hero status — large GO LIVE when offline, pulsing live bar when live */}
      {isLive ? (
        <div
          className="ff-live-bar flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-5"
          data-testid="dashboard-live-bar"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="ff-live-dot flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                {"You're live now"}
              </div>
              <div className="text-base font-semibold text-emerald-900 truncate" data-testid="dashboard-live-location">
                {currentLocName}
              </div>
              {data?.current_location?.address && (
                <div className="text-xs text-emerald-700/80 truncate">
                  {data.current_location.address}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={handleGoOffline}
            disabled={busy}
            className="bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold px-6 h-11"
            variant="outline"
            data-testid="dashboard-go-offline"
          >
            <Power size={16} className="mr-2" /> GO OFFLINE
          </Button>
        </div>
      ) : (
        <div
          className="bg-white border border-slate-200 rounded-xl px-6 py-10 flex flex-col items-center justify-center text-center shadow-sm"
          data-testid="dashboard-go-live-hero"
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {"You're offline"}
          </div>
          <p className="mt-1 text-sm text-slate-500 max-w-md">
            Set a location and broadcast yourself live so hungry customers can find you on the map.
          </p>
          <Link to="/app/location" className="mt-5" data-testid="dashboard-go-live-cta">
            <Button
              className="ff-btn-primary h-16 px-12 text-lg font-bold tracking-wide rounded-full"
              data-testid="dashboard-go-live-button"
            >
              GO LIVE <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Power}
          label="Today's status"
          value={isLive ? "Live" : "Offline"}
          hint={isLive ? "Customers can see you on the map" : "Open the Location page to go live"}
          testid="stat-status"
          accent={isLive}
        />
        <StatCard
          icon={MapPin}
          label="Current location"
          value={isLive ? currentLocName : "—"}
          hint={isLive ? data?.current_location?.address : "Not broadcasting"}
          testid="stat-location"
        />
        <StatCard
          icon={Utensils}
          label="Menu items"
          value={data?.menu_items_count ?? 0}
          hint="Across your full menu"
          testid="stat-menu-count"
        />
        <StatCard
          icon={CalendarDays}
          label="Upcoming events"
          value={data?.upcoming_events_count ?? 0}
          hint="Scheduled going forward"
          testid="stat-events-count"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="card-today-hours">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Clock size={16} />
            <span>{"Today's hours · "}{data?.today_day}</span>
          </div>
          <div className="mt-2 text-xl font-semibold tracking-tight">{hoursLabel}</div>
          {todayHours?.address && (
            <div className="mt-1 text-sm text-slate-500">{todayHours.address}</div>
          )}
          <div className="mt-4">
            <Link to="/app/location" className="text-sm text-[#E8650A] font-semibold" data-testid="link-edit-hours">
              Edit schedule →
            </Link>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="card-quick-actions">
          <div className="text-slate-500 text-sm">Quick actions</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/app/menu" data-testid="quick-add-menu">
              <Button variant="outline" className="w-full justify-start">
                <Utensils size={14} className="mr-2" /> Add menu item
              </Button>
            </Link>
            <Link to="/app/events" data-testid="quick-add-event">
              <Button variant="outline" className="w-full justify-start">
                <CalendarDays size={14} className="mr-2" /> Add event
              </Button>
            </Link>
            <Link to="/app/awards" data-testid="quick-add-award">
              <Button variant="outline" className="w-full justify-start">
                <CalendarDays size={14} className="mr-2" /> Add award
              </Button>
            </Link>
            <Link to="/app/profile" data-testid="quick-edit-profile">
              <Button variant="outline" className="w-full justify-start">
                <MapPin size={14} className="mr-2" /> Edit profile
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <ProFeaturesGrid tier={vendor?.tier} />
    </div>
  );
}
